/**
 * Stakeholder Value Component
 * 
 * Displays value creation metrics for stakeholder groups:
 * - Shareholder Value (ratio + entropy score)
 * - Employee Value (ratio + entropy score)
 * - Community Value (ratio + entropy score)
 * 
 * Entropy Score: Measures coordination loss (1=best, 10=worst)
 * 
 * Used at the Stakeholder Control Domain.
 * @see docs/KRYSAIS_CONTROL_GLOSSARY.md for terminology definitions
 */

import React from 'react';
import { Tooltip } from 'antd';
import {
  BankOutlined,
  UserOutlined,
  HeartOutlined,
  RiseOutlined,
  FallOutlined,
  MinusOutlined
} from '@ant-design/icons';
import { BaseReportingComponent, ComponentData, ReportingComponentProps } from './BaseReportingComponent';
import { ValueMetricDisplay, getEntropyInfo, ValueType } from '../../Shared/ValueMetricDisplay';
import styles from './StakeholderValueComponent.module.css';

export type StakeholderTypeKey = 'shareholder' | 'employees' | 'community';

// Convert backend assessment_rank (higher=better) to entropy score (lower=better)
const toEntropyScore = (assessmentRank: number): number => 11 - assessmentRank;

interface StakeholderMetric {
  type: StakeholderTypeKey;
  label: string;
  icon: React.ReactNode;
  color: string;
  ratio: number;
  /** Entropy score (1-10, lower=better). Measures coordination loss. */
  entropyScore: number;
  trend?: 'up' | 'down' | 'stable';
}

interface StakeholderValueComponentProps extends Omit<ReportingComponentProps, 'componentType'> {
  /** Whether to show compact view */
  compact?: boolean;
  /** Currently selected stakeholder type (for detail expansion) */
  selectedStakeholderType?: StakeholderTypeKey | null;
  /** Callback when a stakeholder type is clicked */
  onStakeholderTypeSelect?: (type: StakeholderTypeKey | null) => void;
}

const getStakeholderMetrics = (data: Record<string, any>): StakeholderMetric[] => {
  return [
    {
      type: 'shareholder',
      label: 'Shareholder Value',
      icon: <BankOutlined />,
      color: '#10b981', // Green
      ratio: data.shareholder_value_ratio ?? 0,
      entropyScore: toEntropyScore(data.shareholder_assessment_rank ?? 5),
      trend: data.shareholder_trend as 'up' | 'down' | 'stable' | undefined
    },
    {
      type: 'employees',
      label: 'Employee Value',
      icon: <UserOutlined />,
      color: '#3b82f6', // Blue
      ratio: data.employees_value_ratio ?? 0,
      entropyScore: toEntropyScore(data.employees_assessment_rank ?? 5),
      trend: data.employees_trend as 'up' | 'down' | 'stable' | undefined
    },
    {
      type: 'community',
      label: 'Community Value',
      icon: <HeartOutlined />,
      color: '#ec4899', // Pink
      ratio: data.community_value_ratio ?? 0,
      entropyScore: toEntropyScore(data.community_assessment_rank ?? 5),
      trend: data.community_trend as 'up' | 'down' | 'stable' | undefined
    }
  ];
};

const TrendIcon: React.FC<{ trend?: 'up' | 'down' | 'stable' }> = ({ trend }) => {
  switch (trend) {
    case 'up':
      return <RiseOutlined style={{ color: '#10b981' }} />;
    case 'down':
      return <FallOutlined style={{ color: '#ef4444' }} />;
    default:
      return <MinusOutlined style={{ color: '#6b7280' }} />;
  }
};

const StakeholderMetricCard: React.FC<{ 
  metric: StakeholderMetric; 
  compact?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
}> = ({ 
  metric, 
  compact = false,
  isSelected = false,
  onClick
}) => {
  const entropyInfo = getEntropyInfo(metric.entropyScore);
  const isClickable = !!onClick;
  
  // Map stakeholder types to ValueType for the display component
  const valueTypeMap: Record<StakeholderTypeKey, ValueType> = {
    shareholder: 'stakeholder',
    employees: 'stakeholder',
    community: 'stakeholder'
  };
  
  return (
    <div 
      className={`${styles.metricCard} ${compact ? styles.compact : ''} ${isSelected ? styles.selected : ''} ${isClickable ? styles.clickable : ''}`}
      style={{ '--metric-color': metric.color } as React.CSSProperties}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => e.key === 'Enter' && onClick?.() : undefined}
    >
      <div className={styles.metricHeader}>
        <div className={styles.metricIcon} style={{ backgroundColor: metric.color }}>
          {metric.icon}
        </div>
        <div className={styles.metricTitle}>
          <span className={styles.metricLabel}>{metric.label}</span>
          {metric.trend && (
            <Tooltip title={`Trend: ${metric.trend}`}>
              <span className={styles.trendIcon}>
                <TrendIcon trend={metric.trend} />
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      <div className={styles.metricBody}>
        <ValueMetricDisplay
          valueRatio={metric.ratio}
          entropyScore={metric.entropyScore}
          valueType={valueTypeMap[metric.type]}
          variant={compact ? 'compact' : 'standard'}
          showLabels={!compact}
        />
      </div>
      
      {isClickable && (
        <div className={styles.expandIndicator}>
          {isSelected ? 'Click to collapse' : 'Click for details'}
        </div>
      )}
    </div>
  );
};

export const StakeholderValueComponent: React.FC<StakeholderValueComponentProps> = ({
  compact = false,
  selectedStakeholderType,
  onStakeholderTypeSelect,
  ...props
}) => {
  const handleCardClick = (type: StakeholderTypeKey) => {
    if (!onStakeholderTypeSelect) return;
    
    // Toggle selection: if already selected, deselect; otherwise select
    if (selectedStakeholderType === type) {
      onStakeholderTypeSelect(null);
    } else {
      onStakeholderTypeSelect(type);
    }
  };

  const renderContent = (data: ComponentData, loading: boolean) => {
    const metrics = getStakeholderMetrics(data.data);
    
    // Find worst performing metric (highest entropy = most coordination loss)
    const worstPerformer = metrics.reduce((worst, m) => m.entropyScore > worst.entropyScore ? m : worst);
    
    // Check if balanced (all within 2 entropy points of each other)
    const scores = metrics.map(m => m.entropyScore);
    const isBalanced = Math.max(...scores) - Math.min(...scores) <= 2;
    
    return (
      <div className={`${styles.stakeholderValueComponent} ${compact ? styles.compact : ''}`}>
        <div className={styles.header}>
          <h3 className={styles.title}>Stakeholder Value Creation</h3>
          <span className={styles.artifactName}>{data.artifact_name}</span>
        </div>

        <div className={styles.metricsGrid}>
          {metrics.map((metric) => (
            <StakeholderMetricCard 
              key={metric.type} 
              metric={metric} 
              compact={compact}
              isSelected={selectedStakeholderType === metric.type}
              onClick={onStakeholderTypeSelect ? () => handleCardClick(metric.type) : undefined}
            />
          ))}
        </div>

        {/* Summary Section */}
        {!compact && (
          <div className={styles.summary}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Stakeholder Balance</span>
              <span className={styles.summaryValue}>
                {isBalanced ? 'Balanced' : 'Needs Attention'}
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Highest Entropy</span>
              <span className={styles.summaryValue}>
                {worstPerformer.label}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <BaseReportingComponent
      {...props}
      componentType="stakeholder_value"
    >
      {renderContent}
    </BaseReportingComponent>
  );
};

export default StakeholderValueComponent;
